import { useMemo } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';

import { usePromotePageData, usePromotePageState } from 'src/app/pages/promote/pageContext';
import { useActions, useTracking } from 'src/app/pages/promote/shared/hooks';

import { useValidationError } from '../../../../hooks/useValidationError';

type Result = ModelHookResult<
	{
		amount: number;
		amountErrorMessage?: string;
		currencySymbol: string;
		currencySymbolPosition: 'before' | 'after';
		decimalSeparator: string;
		showFractionNumber: boolean;
		isSelected: boolean;
		label: string;
	},
	{ setAmount: (value: string) => void }
>;

export function useAmountInput(): Result {
	const { getCurrencySymbol, getCurrencySymbolPosition, getDecimalSeparator, isZeroDecimalCurrency } = useI18n();
	const {
		data: {
			currency: { code },
			amountChoices,
		},
	} = usePromotePageData();

	const [{ amount }] = usePromotePageState();
	const { setAmount } = useActions();
	const { translate } = useI18n();

	const { trackAmountChanged } = useTracking();

	const currencySymbol = getCurrencySymbol(code);
	const currencySymbolPosition = getCurrencySymbolPosition(code);
	const decimalSeparator = getDecimalSeparator();
	const showFractionNumber = !isZeroDecimalCurrency(code);

	const { error } = useValidationError({ type: 'custom' });

	const isSelected = useMemo(() => {
		if (amount === 0) return false;
		return !amountChoices.some((choice) => choice === amount);
	}, [amount, amountChoices]);

	const maybeUpdateAmount = (value: string): void => {
		if (!/^\d*$/.test(value.toString())) return;

		const valueNum = Number(value);
		if (valueNum > Number.MAX_SAFE_INTEGER) return;

		setAmount(valueNum);
		trackAmountChanged({ buttonWasClicked: false });
	};

	return {
		data: {
			amount,
			amountErrorMessage: error,
			currencySymbol,
			currencySymbolPosition,
			decimalSeparator,
			showFractionNumber,
			isSelected,
			label: translate('fe.pages.sponsors.other_amount'),
		},
		actions: {
			setAmount: maybeUpdateAmount,
		},
	};
}
